import { Fragment, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable, ExpandedState } from "@tanstack/react-table";

import { CategoryWithParts, IPart } from "../../../../models/Part";
import { ChevronRightIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import LeadPartsCategoriesTableSubRow from "./LeadPartsCategoriesTableSubRow";
import { DateTime } from "luxon";
import LoadingPulse from "../../../shared/LoadingPulse";
import { LastSoldPriceButton, SmartPriceButton, PartProPriceButton } from "../../../shared/PriceButtons";
import formatDateTime from "../../../../utils/formatDateTime";
import EbayLinkButton from "../../../shared/Inputs/EbayLinkButton";
import { IVehicle } from "../../../../models/Vehicle";
import TableCheckboxCol from "../../../shared/Tables/IndeterminateCheckbox";
import useVehicleCategoryPartsOptimized, { IVehicleCategoryPart } from "../../../../data/useVehicleCategoryPartsOptimized";
import useSingleSetting from "../../../../data/useSingleSetting";
import RsPriceButton from "../../../shared/PriceButtons/RsPriceButton";

const colHelper = createColumnHelper<CategoryWithParts>();

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}


function EditablePriceCell({ rowId, value, handleSetTableData }: { rowId: string; value: number; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localPriceValue, setLocalPriceValue] = useState<number>(value);

  // Whenever the parent value changes (e.g. clicking the Suggested Price button),
  // update localValue so the input shows the latest.
  useEffect(() => {
    setLocalPriceValue(value);
  }, [value]);

  return (
    <input
      type="number"
      value={localPriceValue}
      min={0}
      onChange={(e) => {
        // If user clears the field, treat as 0
        const raw = e.target.value;
        const parsed = raw === "" ? 0 : parseFloat(raw);
        setLocalPriceValue(Number.isNaN(parsed) ? 0 : parsed);
      }}
      onBlur={() => {
        // Commit back into tableData on blur
        if (localPriceValue !== value) {
          handleSetTableData(rowId, localPriceValue);
        }
      }}
      className="border p-1 w-full"
    />
  );
}

function EditableNoteCell({ rowId, value, handleSetTableData }: { rowId: string; value: string; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localNoteValue, setLocalNoteValue] = useState<string>(value);

  // Keep local state in sync if parent value changes (e.g., external updates)
  useEffect(() => {
    setLocalNoteValue(value ?? "");
  }, [value]);

  return (
    <textarea
      value={localNoteValue}
      rows={1}
      onChange={(e) => {
        setLocalNoteValue(e.target.value);
      }}
      onBlur={() => {
        // Commit back into tableData on blur
        if (localNoteValue !== value) {
          handleSetTableData(rowId, undefined, localNoteValue);
        }
      }}
      className="border p-1 text-sm w-full"
    />
  );
}

export interface LeadPartsCategoriesTableProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<CategoryWithParts[]>>;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
}

export default function LeadPartsCategoriesTable({ categories, selectedCategories, setSelectedCategories, partProUnlocked, partProLoading, vehicle, filter }: LeadPartsCategoriesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<CategoryWithParts[]>(categories);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const [hasRsKey, setHasRsKey] = useState(false);

  const filteredTableData = useMemo(() => {
    return filter ? tableData.filter(filter) : tableData;
  }, [tableData, filter]);

  // Sync expandedCategoryIds with expanded state changes from table
  useEffect(() => {
    if (typeof expanded === 'boolean') {
      setExpandedCategoryIds(new Set());
      return;
    }
    const expandedIds = Object.keys(expanded).filter(key => expanded[key]);
    setExpandedCategoryIds(new Set(expandedIds));
  }, [expanded]);

  // Fetch parts for expanded categories
  const expandedCategoryId = Array.from(expandedCategoryIds)[0]; // For now, handle one at a time
  const { parts: categoryParts } = useVehicleCategoryPartsOptimized(
    vehicle?.id,
    expandedCategoryId,
    true, // includeSold
    50 // limitSold
  );

  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedCategories?.forEach((cat) => {
      sel[cat.category.id] = true;
    });
    return sel;
  }, [selectedCategories]);

  const handleSetTableData = useCallback((
    rowId: string, 
    newPrice: number | undefined, 
    newNote: string | undefined = undefined,
    isCore = false // Is refurbished / Rs
  ) => {
    setTableData((old) => {
      let updatedRow: CategoryWithParts | undefined;

      const newArray = old.map((item) => {
        if (item.category.id === rowId) {
          updatedRow = { ...item };

          if (typeof newPrice === "number" && !Number.isNaN(newPrice)) {
            updatedRow.price = newPrice;
          }
          if (typeof newNote !== "undefined") {
            // Allow empty string to clear the note intentionally
            updatedRow.note = newNote;
          }

          updatedRow.isCore = isCore;

          return updatedRow;
        }
        return item;
      });

      //This function is called here with an updater, but goes all the way back up to Lead Parts
      setSelectedCategories((prevSelectedForThisTable) => {
        const isCurrentlySelected = prevSelectedForThisTable.some(
          (cat) => cat.category.id === rowId
        );

        // If a numeric price was provided, handle selection add/update/remove based on price
        if (typeof newPrice === "number" && !Number.isNaN(newPrice)) {
          if (newPrice <= 0 && isCurrentlySelected) {
            // Remove from selection if price is now invalid
            return prevSelectedForThisTable.filter((cat) => cat.category.id !== rowId);
          }

          if (newPrice > 0 && updatedRow) {
            if (!isCurrentlySelected) {
              // If it was not selected, add it
              return [...prevSelectedForThisTable, updatedRow];
            } else {
              // If it was selected, update it
              return prevSelectedForThisTable.map((cat) =>
                cat.category.id === rowId ? updatedRow! : cat
              );
            }
          }
        }

        // If only the note changed, propagate the updated note into the selected item
        if (typeof newPrice !== "number" && typeof newNote !== "undefined" && updatedRow && isCurrentlySelected) {
          return prevSelectedForThisTable.map((cat) =>
            cat.category.id === rowId ? updatedRow! : cat
          );
        }

        // Otherwise, no change
        return prevSelectedForThisTable;
      });

      return newArray;
    });
  }, [setSelectedCategories]);

  const makeModelString = useMemo(() => {
    let string = "";

    if (vehicle?.dvlaYearOfManufacture) {
      string = vehicle?.dvlaYearOfManufacture + "+";
    }

    string += vehicle?.combinedMake;
    string += "+";
    string += vehicle?.modelRangeDescription;

    return string;
  }, [vehicle])

  const baseCols = [
    {
      id: 'dropdown',
      cell: ({ row }: any) => {
        const categoryId = row.original.category.id;
        const isExpanded = row.getIsExpanded();
        
        return (
          <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              style={{ transform: `rotate(${!isExpanded ? "0" : "-270"}deg)` }}
              className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    colHelper.accessor("category.name", {
      id: "category.name",
      header: "Name",
      cell: (info) =>
        <div className="flex gap-x-2 items-center justify-between">
          <p>{info.getValue()}</p>
          <EbayLinkButton query={makeModelString + "+" + info.getValue()} />
        </div>,
    }),
    colHelper.display({
      id: "lookupMpn",
      header: "MPN",
      cell: (info) => {
        if (partProLoading) return <LoadingPulse width="w-full" />;

        let lookupPartNumber = info.row.original.category.lookupPartNumber;
        return (
          <span>{partProUnlocked ?
            (
              <div className="flex gap-x-2 items-center justify-between ">
                <span>{lookupPartNumber ?? "-"}</span>
                {lookupPartNumber && <EbayLinkButton query={lookupPartNumber} />}
              </div>
            ) :
            (<span><LockClosedIcon height={15} width={15} /></span>)
          }</span>
        );
      }
    }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.internalStock !== undefined) {
          return row.internalStock;
        }
        return row.parts?.filter(part => !part.soldDate).length ?? 0;
      },
      {
        id: "partsCount",
        header: "Stock",
        cell: (info) => {
          const partsCount = info.getValue() as number;
          const partProCount = 0; //don't have this data yet
          return (
            <div>
              <p>
                <span className="text-gray-600 text-sm">
                  Internal:{" "}
                </span>
                <span>
                  {partsCount > 0 ? partsCount : "-"}
                </span>
              </p>
              <p className="flex items-center gap-x-1">
                <span className="text-gray-600 text-sm">
                  Part Pro:{" "}
                </span>
                {partProLoading ? (
                  <span className="-mr-10">
                    <LoadingPulse width="w-10" />
                  </span>
                ) : partProUnlocked ? (
                  <span className="text-wilma-500 font-semibold">
                    {partProCount > 0 ? partProCount : "-"}
                  </span>
                ) : (
                  <span className=""><LockClosedIcon height={15} width={15} /></span>
                )}
              </p>
            </div>
          );
        }
      }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.totalSold !== undefined) {
          return row.totalSold;
        }
        return row.parts?.filter(x => x.soldDate)?.length ?? 0;
      },
      {
        id: "soldQuantity",
        header: "Total Previously Sold",
        cell: (info) => {
          const row = info.row.original;
          // Use aggregate field if available, otherwise compute from parts
          const lastSold = row.lastSoldDate ?? row.parts?.filter(x => x.soldDate)?.sort((a, b) => (a.soldDate! < b.soldDate! ? 1 : -1))[0]?.soldDate;
          return (
            <div>
              <p>{info.getValue() ?? "-"}</p>
              {lastSold && (<p className="text-gray-600 text-sm">Last Sold: {formatDateTime(lastSold, DateTime.DATE_MED)}</p>)}
            </div>
          )
        }
      }),
    colHelper.accessor("note",
      {
        id: "note",
        header: "Internal Note",
        enableSorting: false,
        cell: (info) => {
          const currentValue = info.getValue();
          const rowId = info.row.original.category.id;
          return (
            <EditableNoteCell
              rowId={rowId}
              value={currentValue ?? ""}
              handleSetTableData={handleSetTableData}
            />
          );
        }
      }),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.lastSoldPrice !== undefined && row.lastSoldPrice !== null) {
          return row.lastSoldPrice;
        }
        return row.parts?.find((part: IPart) => part.soldDate)?.price || 0;
      },
      {
        id: "lastSoldPriceButton",
        header: "Last Sold",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <LastSoldPriceButton
              parts={row.parts ?? []}
              onPriceSelect={(price) => handleSetTableData(rowId, price)}
              lastSoldPrice={row.lastSoldPrice ?? row.category.lastSoldPrice}
            />
          );
        },
      }
    ),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        const basePrice = row.lastSoldPrice ?? (row.parts?.find((part: IPart) => part.soldDate)?.price || 0);
        return parseFloat((basePrice * 1.1).toFixed(2));
      },
      {
        id: "marketPriceButton",
        header: "Market",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <SmartPriceButton
              categoryWithParts={row}
              onPriceSelect={(price) => handleSetTableData(rowId, price)}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "partProPriceButton",
        header: "PartPro",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <PartProPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price)}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => row.price, {
      header: "Price",
      sortingFn: "auto",
      // Use our EditablePriceCell component
      cell: (info) => {
        const currentValue = info.getValue() as number;
        const rowId = info.row.original.category.id;
        return (
          <EditablePriceCell
            rowId={rowId}
            value={currentValue}
            handleSetTableData={handleSetTableData}
          />
        );
      },
    }),
    TableCheckboxCol(false),
  ];

  const rsCols = [
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "rsPriceButton",
        header: "RS",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <RsPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, true)}
            />
          );
        },
      }
    )
  ];

  useEffect(() => {
    setHasRsKey(singleSetting.isLoading ? false : !!singleSetting.data?.data?.value);

  }, [singleSetting.data, singleSetting.isLoading]);

  // slice rsCols into the baseCols after the "PartPro" column
  const cols = useMemo(() => {
    if (hasRsKey) {
      const partProIndex = baseCols.findIndex(col => col.id === "partProPriceButton");
      if (partProIndex === -1) return baseCols; // fallback, should never happen
      const newCols = [...baseCols];
      newCols.splice(partProIndex + 1, 0, ...rsCols);
      return newCols;
    }
    return baseCols;
  }, [handleSetTableData, partProLoading, partProUnlocked, hasRsKey, baseCols, rsCols]);


  const table = useReactTable({
    data: filteredTableData,
    columns: cols,
    getRowId: (row) => row.category.id,
    state: {
      sorting: sorting,
      rowSelection: rowSelection,
      expanded: expanded
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrState) => {
      // If it's a function, call it with the current state to get newState.
      const newState: Record<string, boolean> =
        typeof updaterOrState === "function"
          ? updaterOrState(rowSelection)
          : updaterOrState;

      const newlySelected: CategoryWithParts[] = [];
      for (const [rowId, isSel] of Object.entries(newState)) {
        if (isSel) {
          const found = tableData.find((c) => c.category.id === rowId);
          if (found) newlySelected.push(found);
        }
      }
      setSelectedCategories(newlySelected);
    },
    onExpandedChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setExpanded(prev => {
          const newValue = updaterOrValue(prev);
          // Ensure only one row is expanded at a time
          if (typeof newValue === 'boolean') return {};
          const trueKeys = Object.keys(newValue).filter(key => newValue[key]);
          if (trueKeys.length > 1) {
            // Keep only the last one (most recently expanded)
            const lastKey = trueKeys[trueKeys.length - 1];
            return { [lastKey]: true };
          }
          return newValue;
        });
      } else {
        if (typeof updaterOrValue === 'boolean') {
          setExpanded({});
        } else {
          // Ensure only one row is expanded
          const trueKeys = Object.keys(updaterOrValue).filter(key => updaterOrValue[key]);
          if (trueKeys.length > 1) {
            const lastKey = trueKeys[trueKeys.length - 1];
            setExpanded({ [lastKey]: true });
          } else {
            setExpanded(updaterOrValue);
          }
        }
      }
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // useEffect(() => {
  //   setTableData(filter ? categories.filter(filter) : categories);
  // }, [categories, filter]);

  // Update tableData when parts are fetched
  useEffect(() => {
    if (categoryParts.data?.data && expandedCategoryId) {
      setTableData(prev => {
        return prev.map(cat => {
          if (cat.category.id === expandedCategoryId) {
            // Convert IVehicleCategoryPart to IPart format
            const convertedParts: IPart[] = categoryParts.data!.data.map(part => ({
              id: part.id,
              taskId: 0,
              dataId: null,
              promotionId: null,
              stockNumber: null,
              tagNumber: part.tagNumber,
              title: part.title,
              titleSanitised: part.titleSanitised,
              titleLink: null,
              ebayThumbnail: null,
              cdnThumbnail: part.cdnThumbnail,
              price: part.price,
              suggestedPrice: null,
              enteredPrice: null,
              marketDataPrice: null,
              marketDataChecked: null,
              lastSoldPrice: null,
              margin: part.margin,
              ebayItemNumber: part.ebayItemNumber,
              ebayTradingApiItemId: part.ebayTradingApiItemId,
              ebayInventoryApiOfferId: part.ebayInventoryApiOfferId,
              activePartOfferId: null,
              vehicleNumber: null,
              isVin: false,
              isInternal: part.isInternal,
              isDamaged: false,
              side: 0,
              position: 0,
              grade: null,
              partNumber: part.partNumber,
              oePartNumber: part.oePartNumber,
              supersededPartNumbers: null,
              partsLookupRequestId: null,
              lastPartLookupChecked: null,
              exactLincNumber: null,
              listedDate: null,
              listingProcessing: null,
              soldDate: part.soldDate,
              processedOn: null,
              removedOn: null,
              removedBy: null,
              passedQcOn: null,
              passedQcBy: null,
              imagedOn: null,
              imagedBy: null,
              pricedBy: null,
              pricedOn: null,
              lastStockChecked: null,
              orderId: null,
              pickedOn: null,
              pickedBy: null,
              shippedOn: null,
              shippedBy: null,
              shippingLabelDocumentId: null,
              trackingNumber: null,
              length: null,
              width: null,
              height: null,
              weight: null,
              fullName: null,
              addressLine1: null,
              addressLine2: null,
              city: null,
              county: null,
              stateOrProvince: null,
              postcode: null,
              partStatus: 0,
              statusMessage: null,
              images: part.images.map(img => ({
                id: img.id,
                url: img.url,
                thumbnailUrl: img.thumbnailUrl,
                isThumbnail: img.isThumbnail,
                useOnEbay: false,
                order: null,
                partDataId: null,
                imageType: null,
                isEndSlate: false,
                endSlateOrder: null,
                deleted: null,
                created: part.created, // Use part's created date
                updated: part.created, // Use part's created date as updated
              })),
              offers: [],
              aspects: [],
              categoryId: cat.category.id,
              category: cat.category,
              categoryAccepted: false,
              vehicleId: part.vehicleId,
              vehicle: part.vehicleId ? {
                id: part.vehicleId,
                combinedMake: part.vehicleCombinedMake,
                combinedModel: part.vehicleCombinedModel,
                vrm: part.vehicleVrm,
                engineModelCode: part.vehicleEngineModelCode,
              } as any : null,
              warehouseLocationId: null,
              warehouseLocation: null,
              shippingPolicyId: null,
              shippingPolicy: null,
              locatedBy: null,
              locatedOn: null,
              notes: [],
              isCore: false,
              internalNotes: [],
              batchItem: null,
              created: part.created,
              updated: part.created,
            }));
            return { ...cat, parts: convertedParts };
          }
          return cat;
        });
      });
    }
  }, [categoryParts.data, expandedCategoryId]);

  return (
    <>
      <div className="mt-4 bg-white overflow-auto space-y-4">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - using nth child to make the name column the same width */}
          {/* Also using first and last to make sure the dropdown and select boxes are the same width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800 [&>*:nth-child(2)]:w-36 [&>*:nth-child(7)]:w-20 [&>*:nth-child(8)]:w-20 [&>*:nth-child(9)]:w-20 [&>*:nth-child(9)]:text-wilma-500 [&>*:nth-child(9)]:font-bold" >
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-16 last:w-16 py-1 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              let noPriceSelected = row.getIsSelected() === true && (!row.original.price || row.original.price <= 0);

              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    // onClick={() => setLocation(row.original)}
                    className={classNames(noPriceSelected ? "bg-red-200" : "", `hover:bg-gray-100`)}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length}>
                        {categoryParts.isLoading && expandedCategoryId === row.original.category.id ? (
                          <div className="p-4 text-center">
                            <LoadingPulse width="w-full" />
                          </div>
                        ) : (
                          <LeadPartsCategoriesTableSubRow categoryWithParts={row.original} />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
}
